import { Injectable, Injector } from '@angular/core';
import {
  CaptchaApiService,
  CaptchaDto,
  EncryptionCaptchaDto,
} from '@prf/interaction';
import { QuerySubjectApiService, QuerySubjectDto } from '@prf/query-subject';
import { TypeCustomerApiService, TypeCustomerDto } from '@prf/type-customer';
import { TypeDocumentApiService, TypeDocumentDto } from '@prf/type-document';
import {
  DataRequestGestionDto,
  DataRequestSaveDto,
  EncryptionDataTreatmentDto,
  WsDataTreatmentApiService,
} from '@prf/ws-data-treatment';
import { from, Observable } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { AuthenticationService } from '../../../services/authentication.service';
import { FacadeBase } from '../../../shared/base/class/facade.base';
import { GlobalState } from '../../../shared/base/class/global.state';
import { EncryptService } from '../../../shared/services/encrypt/encrypt.service';
import { FileService } from '../../../shared/services/file-server/file-server.service';
import { FileResponse } from '../../../shared/services/file-server/models/file-response.dto';
import { FileData } from '../../Models/file-data.model';
import { InteractionResponse } from '../../Models/interaction-response.model';

/**
 * Facade Contact-Us
 */
@Injectable()
export class ContactUsFacade extends FacadeBase {
  /**
   * Servicio encargado de interactuar con el servicio de encriptacion
   */
  private readonly encryptService: EncryptService;
  /**
   * Servicio encargado de interactuar con el file server
   */
  private readonly fileService: FileService;
  /**
   * Servicio encargado de interactuar con el MS de Type Document
   */
  private readonly typeDocumentApiService: TypeDocumentApiService;
  /**
   * Servicio encargado de interactuar con el MS de Type Customer
   */
  private readonly typeCustomerApiService: TypeCustomerApiService;
  /**
   * Servicio encargado de interactuar con el MS de Query Subject
   */
  private readonly querySubjectApiService: QuerySubjectApiService;
  /**
   * Servicio encargado de interactuar con el Ms de Interaction en el endpoint Captcha
   */
  private readonly captchaApiService: CaptchaApiService;
  /**
   * Servicio encargado de interactuar con el Ms de Ws Data Treatment
   */
  private readonly wsDataTreatmentApiService: WsDataTreatmentApiService;
  /**
   * Servicio encargado de administrar la autenticación
   */
  private readonly authenticationService: AuthenticationService;
  /**
   * Observable - Tipos de documentos
   */
  public get typeDocuments$(): Observable<TypeDocumentDto[]> {
    return this.state.typeDocuments$;
  }
  /**
   * Observable - Tipos de cliente
   */
  public get typeCustomers$(): Observable<TypeCustomerDto[]> {
    return this.state.typeCustomers$;
  }
  /**
   * Observable - Asuntos de consulta
   */
  public get querySubjects$(): Observable<QuerySubjectDto[]> {
    return this.state.querySubjects$;
  }
  /**
   * Observable que contiene la información de los archivos subidos
   */
  public get dataSelected$(): Observable<FileData[]> {
    return this.state.fileUpload$;
  }
  /**
   * Observable que contiene la información de los archivos subidos
   */
  public get infoDisableButtonFileUp$(): Observable<boolean> {
    return this.state.disableButtonFileUp$;
  }

  /**
   * Constructor
   * @param state estado global
   * @param injector Servicio para obetner acceso a dependency control
   * @param typeDocumentApiService Servicio encargado  de interactuar con el microservicio de tipo de documento
   * @param typeCustomerApiService Servicio encargado  de interactuar con el microservicio de tipo de cliente
   * @param querySubjectApiService Servicio encargado  de interactuar con el microservicio de asunto de consulta
   * @param captchaApiService Servicio encargado de interactuar con el Ms de Interaction en el endpoint Captcha
   * @param authenticationService Servicio encargado de los datos de Autenticación
   * @param fileService Servicio encargado de enviar los datos al file server
   */
  public constructor(
    state: GlobalState,
    injector: Injector,
    typeDocumentApiService: TypeDocumentApiService,
    typeCustomerApiService: TypeCustomerApiService,
    querySubjectApiService: QuerySubjectApiService,
    wsDataTreatmentApiService: WsDataTreatmentApiService,
    captchaApiService: CaptchaApiService,
    authenticationService: AuthenticationService,
    encryptService: EncryptService,
    fileService: FileService,
  ) {
    super(injector, state);
    this.typeDocumentApiService = typeDocumentApiService;
    this.typeCustomerApiService = typeCustomerApiService;
    this.querySubjectApiService = querySubjectApiService;
    this.wsDataTreatmentApiService = wsDataTreatmentApiService;
    this.captchaApiService = captchaApiService;
    this.authenticationService = authenticationService;
    this.encryptService = encryptService;
    this.fileService = fileService;
  }

  /**
   * Metodo encargado de consultar los tipos de documentos
   */
  public allTypeDocuments(fn: () => void) {
    this.spinner.show();
    this.typeDocumentApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        finalize(() => {
          this.spinner.hide();
          fn();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (response) => {
          this.spinner.hide();
          this.state.setTypeDocuments(response);
        },
        () => {
          this.spinner.hide();
        },
      );
  }

  /**
   * Metodo encargado de consultar los tipos de clientes
   */
  public allTypeCustomers(fn: () => void) {
    this.spinner.show();
    this.typeCustomerApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        map((data) => data.filter((x) => x.state === true)),
        finalize(() => {
          this.spinner.hide();
          fn();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (response) => {
          this.spinner.hide();
          this.state.setTypeCustomers(response);
        },
        () => {
          this.spinner.hide();
        },
      );
  }

  /**
   * Metodo encargado de consultar los Asuntos de consulta
   */
  public allQuerySubjects() {
    this.spinner.show();
    this.querySubjectApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        finalize(() => this.spinner.hide()),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (response) => {
          this.spinner.hide();
          this.state.setQuerySubjects(response);
        },
        () => {
          this.spinner.hide();
        },
      );
  }

  /*
   **Metodo  que trae  los base 64 de tratamiento de datos
   */
  public async getTratamientoDatos(): Promise<Observable<EncryptionDataTreatmentDto>> {
    this.spinner.show();
    const data: DataRequestGestionDto = {
      codigoReferencia: '1004',
      funcionalidad: 'VENTANILLA',
      nombreReferencia: '1',
      producto: 'HABEAS_DATA',
      tipoDocumento: 'TRATAMIENTODATOS',
    };
    const dataRequestSaveJson = JSON.stringify(data);
    const dataRequestSaveEncrypt = await this.encryptService.toEncrypt(dataRequestSaveJson);
    const request: EncryptionDataTreatmentDto = {
      data: dataRequestSaveEncrypt,
    };

    return this.wsDataTreatmentApiService.getWsDataTreatment(request).pipe(takeUntil(this.destroy$));
  }

  /*
   **Metodo  que trae  los base 64 de terminos y condiciones
   */
  public async getTermsAndConditions(): Promise<Observable<EncryptionDataTreatmentDto>> {
    this.spinner.show();
    const data: DataRequestGestionDto = {
      codigoReferencia: '1005',
      funcionalidad: 'VENTANILLA',
      nombreReferencia: '2',
      producto: 'USO_PLATAFORMA',
      tipoDocumento: 'TERMINOSYCONDICIONES',
    };

    const dataRequestSaveJson = JSON.stringify(data);

    const dataRequestSaveEncrypt = await this.encryptService.toEncrypt(dataRequestSaveJson);

    const request: EncryptionDataTreatmentDto = {
      data: dataRequestSaveEncrypt,
    };

    return this.wsDataTreatmentApiService.getWsDataTreatment(request).pipe(takeUntil(this.destroy$));
  }

  /*
   **Metodo para aceptar tratamiento de datos
   */
  public aceptacionTratamientoDatos(tipoAfi: string, numberAfi: string): Observable<EncryptionDataTreatmentDto> {
    this.spinner.show();

    const dataRequestSave: DataRequestSaveDto = {
      aceptaciones: [
        {
          idDocumento: 429,
          aplicacion: 'VENTANILLA',
          ip: '190.253.152.137',
          tipoIdAfil: tipoAfi,
          numeroIdAfil: Number(numberAfi),
          transaccion: 'VENTANILLA',
          conceptoAceptacion: 'HABEAS_DATA_USO_APP',
          respuesta: 'SI',
          usuarioCreacion: '1073680216',
        },
      ],
    };

    const dataRequestSaveJson = JSON.stringify(dataRequestSave);

    return from(this.encryptService.toEncrypt(dataRequestSaveJson)).pipe(
      switchMap((dataRequestSaveEncrypt) => {
        const request: EncryptionDataTreatmentDto = {
          data: dataRequestSaveEncrypt,
        };
        return this.wsDataTreatmentApiService.PostWsDataTreatment(request).pipe(takeUntil(this.destroy$));
      }),
    );
  }
  /*
   **Metodo para aceptar terminos y condiciones
   */
  public acceptanceTemsAndConditions(tipoAfi: string, numberAfi: string): Observable<EncryptionDataTreatmentDto> {
    this.spinner.show();

    const dataRequestSave: DataRequestSaveDto = {
      aceptaciones: [
        {
          idDocumento: 341,
          aplicacion: 'VENTANILLA',
          ip: '10.34.2.98',
          tipoIdAfil: tipoAfi,
          numeroIdAfil: Number(numberAfi),
          transaccion: 'VENTANILLA',
          conceptoAceptacion: 'ACEPTA_TERMINOS',
          respuesta: 'SI',
          usuarioCreacion: 'SISISGO',
        },
      ],
    };

    const dataRequestSaveJson = JSON.stringify(dataRequestSave);

    return from(this.encryptService.toEncrypt(dataRequestSaveJson)).pipe(
      switchMap((dataRequestSaveEncrypt) => {
        const request: EncryptionDataTreatmentDto = {
          data: dataRequestSaveEncrypt,
        };
        return this.wsDataTreatmentApiService.PostWsDataTreatment(request).pipe(takeUntil(this.destroy$));
      }),
    );
  }
  /**
   * Método encargado de consultar el web service de Authentication a fin de validar el Captcha
   * @param captcha Representa la informacion de CaptchaDto
   */
  public async auth(captcha: CaptchaDto, fn: (flag: boolean) => void) {
    this.spinner.show();

    const captchaJson = JSON.stringify(captcha);

    const captchaEncrypt = await this.encryptService.toEncrypt(captchaJson);

    const request: EncryptionCaptchaDto = {
      data: captchaEncrypt,
    };

    this.captchaApiService
      .postCaptcha(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        async (response) => {
          const captchaDecrypt = await this.encryptService.toDecrypt(response.data || '');

          const captchaDeserealized = JSON.parse(captchaDecrypt) as InteractionResponse;

          if (captchaDeserealized.reCaptchaDto.isValidAuthentication) {
            this.state.setReCaptcha(captchaDeserealized.reCaptchaDto);
            this.state.setToken(captchaDeserealized.reCaptchaDto.authenticationToken || '');
            this.authenticationService.setAuthenticated = captchaDeserealized.reCaptchaDto.isValidAuthentication || false;
            this.authenticationService.setToken = captchaDeserealized.reCaptchaDto.authenticationToken || '';
            this.spinner.hide();
            fn(true);
          } else {
            this.spinner.hide();
            fn(false);
          }
        },
        () => {
          fn(false);
          this.spinner.hide();
        },
      );
  }

  /**
   * Metodo encargado de enviar los archivos al file server
   * @param files Lista de los archivos a cargar en el file service
   * @returns lista con las urls y los nombres de los archivos cargados
   */
  public async saveFileStorage(files: FileData[]): Promise<string> {
    try {
      this.spinner.show();
      const filesResponse: FileResponse[] = [];

      const convertedFiles: File[] = files.map((fileData) => {
        // Verificar si el contenido está en formato "tipo,base64"
        let blob: Blob;
        if (fileData.content) {
          try {
            const [type, base64Data] = fileData.content.split(',');
            const binaryData = atob(base64Data || '');
            const byteArray = new Uint8Array(binaryData.length);

            // Convertir la cadena binaria a un array de bytes
            for (let i = 0; i < binaryData.length; i++) {
              byteArray[i] = binaryData.charCodeAt(i);
            }

            blob = new Blob([byteArray], { type });
          } catch (error) {
            blob = new Blob([], { type: fileData.type || 'application/octet-stream' });
          }
        } else {
          blob = new Blob([], { type: fileData.type || 'application/octet-stream' });
        }

        // Crear un objeto File
        return new File([blob], fileData.name || '', {
          lastModified: fileData.lastModified || Date.now(),
          type: fileData.type || blob.type,
        });
      });

      // Llamada al servicio y manejo del resultado
      const response = await this.fileService.PostUpdateFiles(convertedFiles);
      response.forEach((item) => {
        const fileResponse: FileResponse = {
          name: item.fileInfo?.name,
          uriDownload: item.fileInfo?.path?.uriDownload,
        };
        filesResponse.push(fileResponse);
      });
      this.spinner.hide();
      // Devuelve la información como una cadena JSON
      return JSON.stringify(filesResponse);

    } catch (error) {
      this.spinner.hide();
      throw new Error('');
    }
  }
}
